import * as React from 'react';
import debounce from 'lodash/debounce';
import { useDispatch, useSelector } from 'react-redux';
import { CoreClassroomAdminClassroomListing, CoreClassroomAdminClassroomState, CoreClassroomAdminDashboard } from '../../../models/CoreClassroomAdminClassroom';
import { CoreClassroomAdminClassroomFilter, PageResult } from '../../../models/CoreModels';
import { RequestState } from '../../../models/RequestState';
import { RootState } from '../../../store/store';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ListClassroomAdminClassroom } from '../../../store/ClassroomAdmin/ClassroomAdminClassroomAction';
import { getClassroomAdminDashboardInfo } from '../../../store/ClassroomAdmin/ClassroomAdminDashboardAction';
import StatusCode from '../../../util/StatusCode';
import TableHorizontal, { InfoObject } from '../../misc/TableHorizontal';
import { useTranslation } from 'react-i18next';
import FilterBottomComponent from '../../misc/FilterBottomComponent';
import { Link, useSearchParams } from 'react-router-dom';
import RequestParser from '../../../util/RequestParser';
import { formatDate } from '../../../util/Helper';
import Search from '../../misc/Search';
import SearchableSelectInput from '../../misc/SearchableSelectInput';
import { Col, Input, Label, Row } from 'reactstrap';
import { getClassroomAdminClassroomFilters } from '../../../store/FilterAction';
import ClassroomAdminToolsOperation from './Tools/ClassroomAdminToolsOperation';


const ClassroomAdminClassroomlist = () => {
    const { t } = useTranslation(['common', 'classroomAdmin']);
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const filter = useMemo(() => RequestParser.parseClassroomAdminClassroomPageRequest(searchParams), [searchParams.toString()]);
    const [search, setSearch] = useState(filter.search ?? '');

    const adminClassrooms = useSelector<RootState, RequestState<PageResult<CoreClassroomAdminClassroomListing>>>(x => x.classroomAdminClassroom.classrooms);
    const classroomFilter = useSelector<RootState, CoreClassroomAdminClassroomFilter>(x => x.filter.classroomAdminClassroomFilters ?? { schoolUnits: [], statuses: [], syncStates: [] });
    const dashboard = useSelector<RootState, RequestState<CoreClassroomAdminDashboard>>(x => x.classroomAdminDashboard.dashboard);


    useEffect(() => {
        getClassroomAdminClassroomFilters()(dispatch);
    }, [dispatch]);

    useEffect(() => {
        setSearch(filter.search ?? '');
    }, [filter.search]);

    useEffect(() => {
        ListClassroomAdminClassroom(filter)(dispatch);
        getClassroomAdminDashboardInfo(filter)(dispatch);
        window.scrollTo(0, 0);
    }, [dispatch, filter]);

    const updateFilter = useCallback((name: string, value?: number | string | boolean | null) => {
        const nextSearchParams = new URLSearchParams(searchParams);

        if (name !== 'page') {
            nextSearchParams.set('page', '1');
        }

        if (typeof (value) === 'boolean') {
            if (value) {
                nextSearchParams.set(name, value.toString());
            } else {
                nextSearchParams.delete(name);
            }
        } else if (value !== undefined && value !== '' && value !== null) {
            nextSearchParams.set(name, value.toString());
        } else {
            nextSearchParams.delete(name);
        }

        setSearchParams(nextSearchParams);
    }, [searchParams, setSearchParams])

    const debouncedSearch = useCallback(
        debounce((search: string) => {
            updateFilter('search', search);
        }, 300), [updateFilter]);

    const onSearch: React.ChangeEventHandler<HTMLInputElement> = (e) => {
        e.preventDefault();
        const currentSearch = e.target.value;
        setSearch(currentSearch);
        debouncedSearch(currentSearch);
    }

    const clearFilter = () => {
        setSearch('');
        setSearchParams([]);
    }

    const schoolUnitFilterItems = classroomFilter.schoolUnits
        .map(x => ({ value: x.id, label: x.title ?? '' }));

    const statusFilterItems = classroomFilter.statuses
        .map(x => ({ value: x, label: t(`classroomAdmin.filter.statuses.${x}`, { ns: 'classroomAdmin' }) }));

    const syncStateFilterItems = classroomFilter.syncStates
        .map(x => ({ value: x, label: t(`classroomAdmin.filter.syncStates.${x}`, { ns: 'classroomAdmin' }) }));

    const renderFilter = () => (
        <div>
        {renderDashboardRow()}
        <Row>
            <Col sm="3">
                <Search
                    value={search}
                    onChange={onSearch}
                    placeholder={t('classroomAdmin.filter.searchPlaceholder', { ns: 'classroomAdmin' })}
                />
            </Col>
            <Col sm="3">
                <SearchableSelectInput
                    items={schoolUnitFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='schoolUnitId'
                    selected={filter.schoolUnitId}
                    defaultText={t('classroomAdmin.filter.schoolUnits', { ns: 'classroomAdmin' })}
                />
            </Col>
            <Col sm="3">
                <SearchableSelectInput
                    items={statusFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='status'
                    selected={filter.status}
                    defaultText={t('classroomAdmin.filter.status', { ns: 'classroomAdmin' })}
                />
            </Col>
            <Col sm="3">
                <SearchableSelectInput
                    items={syncStateFilterItems}
                    onChange={(name, value) => updateFilter(name, value)}
                    name='syncState'
                    selected={filter.syncState}
                    defaultText={t('classroomAdmin.filter.syncState', { ns: 'classroomAdmin' })}
                />
            </Col>
            </Row>
            <Row className="align-items-center">
            <Col sm="12">
                    <div className="d-flex align-items-baseline gap-2 flex-wrap">
                    <Label htmlFor="createdFrom" className="mb-0 text-nowrap">
                        {t('classroomAdmin.filter.created.title', { ns: 'classroomAdmin' })}
                    </Label>
                    <Input
                        id='createdFrom'
                        type='date'
                        className='w-auto'
                        aria-label={t('classroomAdmin.filter.created.from', { ns: 'classroomAdmin' })}
                        title={t('classroomAdmin.filter.created.from', { ns: 'classroomAdmin' })}
                        value={filter.createdFrom ?? ''}
                        onChange={e => updateFilter('createdFrom', e.target.value || null)}
                    />
                    <span aria-hidden="true">-</span>
                    <Input
                        id='createdTo'
                        type='date'
                        className='w-auto'
                        aria-label={t('classroomAdmin.filter.created.to', { ns: 'classroomAdmin' })}
                        title={t('classroomAdmin.filter.created.to', { ns: 'classroomAdmin' })}
                        value={filter.createdTo ?? ''}
                        onChange={e => updateFilter('createdTo', e.target.value || null)}
                    />
                    <div className="d-flex align-items-center gap-2 flex-wrap ms-sm-2">
                        <div className="form-check mb-0">
                            <Input
                                id='missingTeachers'
                                type='checkbox'
                                className='form-check-input'
                                checked={filter.missingTeachers ?? false}
                                onChange={e => updateFilter('missingTeachers', e.target.checked)}
                            />
                            <Label className='form-check-label text-nowrap' htmlFor='missingTeachers'>
                                {t('classroomAdmin.filter.missingTeachers', { ns: 'classroomAdmin' })}
                            </Label>
                        </div>
                        <div className="form-check mb-0">
                            <Input
                                id='missingStudents'
                                type='checkbox'
                                className='form-check-input'
                                checked={filter.missingStudents ?? false}
                                onChange={e => updateFilter('missingStudents', e.target.checked)}
                            />
                            <Label className='form-check-label text-nowrap' htmlFor='missingStudents'>
                                {t('classroomAdmin.filter.missingStudents', { ns: 'classroomAdmin' })}
                            </Label>
                        </div>
                    </div>
                </div>
            </Col>
        </Row>
        </div>
    );

    const buildLink = useCallback((overrides: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        next.set('page', '1');

        Object.entries(overrides).forEach(([k, v]) => {
            if (v === null) {
                next.delete(k);
            } else {
                next.set(k, v);
            }
        });

        return `/classroomadmin/classroom?${next.toString()}`;
    }, [searchParams]);

    type CountSelector = (data: CoreClassroomAdminDashboard) => number;
    interface DashboardInfo {
        key: string
        name: string
        link: string
        selector: CountSelector
    }

    const dashboardInfo: DashboardInfo[] = [
        { key: 'classrooms', name: t('classroomAdmin.dashboard.stats.classrooms', { ns: 'classroomAdmin' }), link: buildLink({ status: null, syncState: null, missingTeachers: null }), selector: d => d.classrooms },
        { key: 'synched', name: t('classroomAdmin.dashboard.stats.synched', { ns: 'classroomAdmin' }), link: buildLink({ syncState: '1' }), selector: d => d.synched },
        { key: 'orphanRisk', name: t('classroomAdmin.dashboard.stats.orphanRisk', { ns: 'classroomAdmin' }), link: buildLink({ status: '1' }), selector: d => d.orphanRisk },
        { key: 'orphaned', name: t('classroomAdmin.dashboard.stats.orphaned', { ns: 'classroomAdmin' }), link: buildLink({ status: '2' }), selector: d => d.orphaned },
        { key: 'missingTeachers', name: t('classroomAdmin.dashboard.stats.missingTeachers', { ns: 'classroomAdmin' }), link: buildLink({ missingTeachers: 'true' }), selector: d => d.missingTeachers },
    ];

    const getDashboardCount = (selector: CountSelector): number =>
        dashboard.code === StatusCode.COMPLETE && dashboard.data ? selector(dashboard.data) : 0;

    const renderDashboardRow = () => {
        if (dashboard.code === StatusCode.ERROR) {
            return null;
        }

        return (
            <>
                <div className="list-group list-group-horizontal-lg mb-3">
                    {dashboardInfo.map(entry => (
                        <div key={entry.key} className="list-group-item d-flex justify-content-between align-items-center flex-fill">
                            {entry.name}
                            <span className="badge bg-primary rounded-pill">{getDashboardCount(entry.selector)}</span>
                        </div>
                    ))}
                </div>
            </>
        );
    };

    const getContent = (): InfoObject[] => {

        if (adminClassrooms.code === StatusCode.COMPLETE) {
            const GetState = (courseState: CoreClassroomAdminClassroomState) => {
                return `${t('classroomAdmin.state.' + courseState.courseState, { ns: 'classroomAdmin' })} ${t('classroomAdmin.stateInfo.' + courseState.stateInformation, { ns: 'classroomAdmin' })}`
            }
            return adminClassrooms.data.values.map(x => {
                const ownerName = x.ownerName?.trim();
                const ownerEmail = x.ownerEmail?.trim();
                const owner = ownerName && ownerEmail
                    ? `${ownerName} (${ownerEmail})`
                    : ownerName ?? ownerEmail ?? '';

                return ({
                    id: x.classroomId,
                    data: [
                        { value: <Link to={`/classroomadmin/classroom/${x.classroomId}/information`}>{x.classroomName}</Link> },
                        { value: owner },
                        { value: GetState(x.state)},
                        { value: x.synched === true ? t('yes', { ns: 'common' }) : t('no', { ns: 'common' }) },
                        { value: formatDate(x.creationTime) },
                        { value: x.teacherCount },
                        { value: x.studentCount },
                        { value: <ClassroomAdminToolsOperation classroom={x} />}
                    ]
                })
            })
        } else {
            return [];
        }
    }

    return (
        <div className="container increasedHeight">
            <div className="padding-40" style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: '1rem' }}>
                <h1> {t('classroomAdmin.title', { ns: 'classroomAdmin' })} </h1>
                {renderFilter()}
            </div>
                <div style={{ marginBottom: '1.6rem' }}>
                <TableHorizontal
                    header={[
                        { value: t('classroomAdmin.tableHeader.classroomName', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.owner', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.classroomState', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.synched', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.creationTime', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.teacherCount', { ns: 'classroomAdmin' }) },
                        { value: t('classroomAdmin.tableHeader.studentCount', { ns: 'classroomAdmin' }) },
                        { value: t('tools.title', { ns: 'classroomAdmin' }) }
                    ]}
                    info={getContent()}
                    error={adminClassrooms.code === StatusCode.ERROR}
                    searching={adminClassrooms.code === StatusCode.PENDING}
                    noResMsg={t('classroomAdmin.notFound.classroomListing', { ns: 'classroomAdmin' })}  
                />
                </div>
            </div>
            <FilterBottomComponent
                clearFilter={clearFilter}
                updateFilter={(value) => updateFilter('page', value)}
                requestState={adminClassrooms}
            />

        </div>
    )
}
export default ClassroomAdminClassroomlist; 